import type { ApiSchemas } from '@provisium/web-api'

import { apiRequest } from './client'
import type {
  RfqLineDto,
  SupplierRfqInboxItemDto,
  SupplierRfqInboxPageDto,
  SupplierRfqRejectionReasonCode,
} from './types'

// Тип провода берём из контракта: он генерируется из openapi.json, который печётся
// из кода бэкенда. Уберут или переименуют поле — здесь будет ошибка компиляции,
// а не тихий undefined в рантайме.
// camelCase-часть — совместимость со старым бэкендом (до раскатки snake в прод),
// снимается на шаге 4.
type RfqLineRaw = ApiSchemas['RfqLineDto'] & Partial<RfqLineDto>
type SupplierInboxItemRaw = ApiSchemas['SupplierRfqInboxItemDto'] &
  Partial<SupplierRfqInboxItemDto> & {
    // Внутренняя модель зовёт покупателя restaurant*, провод — buyer* (2aa43db),
    // поэтому camelCase-дублей в Partial<> нет, объявляем руками.
    buyerOrganizationId?: number
    buyerOrganizationName?: string
    lines?: RfqLineRaw[]
  }

const normalizeLine = (raw: RfqLineRaw): RfqLineDto => ({
  // Поля в контракте опциональные — типы из спеки это показали, обрабатываем undefined.
  id: raw.id ?? 0,
  productTypeId: raw.productTypeId ?? raw.product_type_id ?? 0,
  quantity: Number(raw.quantity ?? 0),
  unit: raw.unit ?? '',
  note: raw.note ?? null,
  sortOrder: raw.sortOrder ?? raw.sort_order ?? 0,
  supplierSku: raw.supplierSku ?? raw.supplier_sku ?? null,
  productName: raw.productName ?? raw.product_name ?? null,
  lineStatus: raw.lineStatus ?? raw.line_status ?? 'active',
})

const normalizeInboxItem = (raw: SupplierInboxItemRaw): SupplierRfqInboxItemDto => ({
  inboxId: raw.inboxId ?? raw.inbox_id ?? 0,
  rfqId: raw.rfqId ?? raw.rfq_id ?? 0,
  // На проводе это buyer_organization_* (переименовано в 2aa43db). Внутренняя модель
  // осталась restaurant* — переименование фронта отдельной задачей.
  restaurantOrganizationId: raw.buyerOrganizationId ?? raw.buyer_organization_id ?? 0,
  restaurantOrganizationName:
    raw.buyerOrganizationName ?? raw.buyer_organization_name ?? 'Ресторан',
  restaurantLegalName: raw.restaurantLegalName ?? raw.restaurant_legal_name ?? null,
  restaurantTradeName: raw.restaurantTradeName ?? raw.restaurant_trade_name ?? null,
  rfqTitle: raw.rfqTitle ?? raw.rfq_title ?? 'Заявка',
  rfqDescription: raw.rfqDescription ?? raw.rfq_description ?? null,
  rfqStatus: raw.rfqStatus ?? raw.rfq_status ?? 'open',
  inboxStatus: raw.inboxStatus ?? raw.inbox_status ?? 'unread',
  decisionStatus: raw.decisionStatus ?? raw.decision_status ?? 'pending',
  decisionComment: raw.decisionComment ?? raw.decision_comment ?? null,
  decisionAt: raw.decisionAt ?? raw.decision_at ?? null,
  completedAt: raw.completedAt ?? raw.completed_at ?? null,
  createdAt: raw.createdAt ?? raw.created_at ?? new Date(0).toISOString(),
  desiredDeliveryDate: raw.desiredDeliveryDate ?? raw.desired_delivery_date ?? null,
  desiredDeliveryTimeUntil:
    raw.desiredDeliveryTimeUntil ?? raw.desired_delivery_time_until ?? null,
  deliveryAddress: raw.deliveryAddress ?? raw.delivery_address ?? null,
  rejectionReasonCode: raw.rejectionReasonCode ?? raw.rejection_reason_code ?? null,
  substitutionComment: raw.substitutionComment ?? raw.substitution_comment ?? null,
  restaurantSubstitutionComment:
    raw.restaurantSubstitutionComment ?? raw.restaurant_substitution_comment ?? null,
  customerConfirmedAt: raw.customerConfirmedAt ?? raw.customer_confirmed_at ?? null,
  customerDisputeAt: raw.customerDisputeAt ?? raw.customer_dispute_at ?? null,
  customerDisputeComment: raw.customerDisputeComment ?? raw.customer_dispute_comment ?? null,
  lines: (raw.lines ?? []).map(normalizeLine),
})

export const listSupplierInbox = async (
  organizationId: number,
  page = 0,
  size = 50,
): Promise<SupplierRfqInboxPageDto> => {
  const pageDto = await apiRequest<SupplierRfqInboxPageDto & { items?: SupplierInboxItemRaw[] }>(
    `/supplier/rfqs/inbox?organizationId=${organizationId}&page=${page}&size=${size}`,
  )
  return {
    ...pageDto,
    items: (pageDto.items ?? []).map(normalizeInboxItem),
  }
}

export const decideSupplierRfq = (
  inboxId: number,
  organizationId: number,
  action: 'accept' | 'reject' | 'propose_substitution',
  options?: { message?: string; reasonCode?: SupplierRfqRejectionReasonCode },
) =>
  apiRequest<void>(`/supplier/rfqs/inbox/${inboxId}/decision?organizationId=${organizationId}`, {
    method: 'PATCH',
    body: {
      action,
      message: options?.message?.trim() || undefined,
      reason_code: options?.reasonCode,
    },
  })

export const completeSupplierRfq = (
  inboxId: number,
  organizationId: number,
  message?: string,
) =>
  apiRequest<void>(`/supplier/rfqs/inbox/${inboxId}/complete?organizationId=${organizationId}`, {
    method: 'PATCH',
    body: { message: message?.trim() || undefined },
  })
